<script lang="ts">
	//Stores
	import { sidepanelChartMode, sidepanelDesktopOnTop, sidePanelState, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { app, os } from 'src/stores/session/native-bridge';
	import { devices } from 'src/stores/lists/devices';
	import { routeParams } from 'src/stores/ui/viewport';
	//Helpers
	import { force_go_back } from 'src/services/native_container_requests_android';
	import { backForwardNavigation } from 'src/services/native_container_requests_ios';
	import { setSidePanelState } from './sidepanel-helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { hasSettingsRights } from 'src/helpers/user_helpers';
	//Design System Components
	import { Sidepanel } from '@Shelly/shelly-design-system/index';
	//Components
	import SidePanelLayoutProvider from './SidePanelLayoutProvider/SidePanelLayoutProvider.svelte';

	let sidepanelHTMLRef: HTMLElement;
	/**
	 * The DS `Sidepanel` fires `onClose` once on mount because its reactive
	 * statement runs with `isOpen=false` as soon as `sidepanelHTMLRef` is
	 * bound. Without this guard, the `transitionend` listener attached in
	 * `onClose` would still be armed when the user opens the sidepanel for
	 * the first time and would fire on the OPEN transition, immediately
	 * re-enabling iOS swipe-back — causing the very first swipe-back from a
	 * device sidepanel to also pop the underlying route (FE-1630).
	 */
	let wasOpen = false;

	const setNativeBackAndForwardNavigation = (enable: boolean) => {
		if ($os === 'IOS') {
			backForwardNavigation(enable);
			return;
		}

		if ($app) force_go_back(!enable);
	};

	const blurActiveElement = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	};

	const onOpen = () => {
		wasOpen = true;
		// disable iOS native back/forward navigation while the drawer is open
		setNativeBackAndForwardNavigation(false);

		blurActiveElement();
	};

	const onClose = () => {
		sidepanelChartMode.set(false);

		setSidePanelState({
			// set drawer default state
			id: $routeParams.id,
			type: $routeParams.type,
			open: false,
		});

		// Skip side effects when the DS Sidepanel calls onClose spuriously
		// on mount (no actual open → close transition is happening).
		if (!wasOpen) return;
		wasOpen = false;

		const updateDrawerStateClose = () => {
			// enable iOS native back/forward navigation when the drawer closes
			setNativeBackAndForwardNavigation(true);

			return sidepanelHTMLRef.removeEventListener('transitionend', updateDrawerStateClose);
		};

		// Used event listener to prevent using timeout
		sidepanelHTMLRef.addEventListener('transitionend', updateDrawerStateClose);

		// REMOVE FOCUSED ELEMENT - clicked before AdvancedCard,ListMenuCard ...
		blurActiveElement();
	};

	// Shared members whose home-level "device settings" right is revoked
	// (`rights.settings === 0`) must not reach a device's settings modal, so the
	// header gear is greyed out and made unclickable for that device (FE-2022).
	$: sidepanelDevice =
		['device', 'devices'].includes($sidePanelStateCombined?.type ?? '') &&
		$sidePanelStateCombined?.id
			? ($devices[$sidePanelStateCombined.id] ?? $devices[real_id($sidePanelStateCombined.id)])
			: undefined;
	$: noDeviceSettingsAccess = !!sidepanelDevice && !hasSettingsRights(sidepanelDevice);
</script>

<Sidepanel
	bind:sidepanelHTMLRef
	isOpen={$sidePanelState.open}
	{onOpen}
	{onClose}
	desktopOnTop={$sidepanelDesktopOnTop}
	background="--color-black"
>
	<div class="sidepanel-rights-scope" class:no-device-settings-access={noDeviceSettingsAccess}>
		<SidePanelLayoutProvider on:close={onClose} />
	</div>
</Sidepanel>

<style>
	.sidepanel-rights-scope {
		display: contents;
	}

	/* The header gear is the second `.sidepanel-btn` after the close button. */
	.sidepanel-rights-scope.no-device-settings-access :global(.sidepanel-btn ~ .sidepanel-btn) {
		opacity: 0.4;
		pointer-events: none;
	}
</style>
